import { UseQueryResult } from '@tanstack/react-query'
import { createContext, useContext } from 'react'
import { useParams } from 'react-router-dom'

import { PatientInfo } from '@/modules/patient/domain/models/PatientInfo'
import { useListPatientsQuery } from '@/modules/patient/presentation/hooks/useListPatientsQuery'
import { usePatientQuery } from '@/modules/patient/presentation/hooks/usePatientQuery'

import { useUserRedirect } from '../hooks/useRedirectRoute'
import { useUserPermission } from '../hooks/useUserPermission'
import { useAuthContext } from './AuthContext'

const patientContext = createContext<{
  patient?: PatientInfo
  patientQuery?: UseQueryResult<PatientInfo, Error>
  isPatientManager: boolean
}>({
  patient: undefined,
  patientQuery: undefined,
  isPatientManager: false
})

export const usePatientContext = () => useContext(patientContext)

export default function PatientProvider({
  children
}: {
  children: React.ReactNode
}) {
  useUserRedirect()

  const { personaSession } = useAuthContext()
  const { patientId } = useParams()
  const { checkPermission } = useUserPermission()

  const patientsQuery = useListPatientsQuery({
    personaId: personaSession?.personaId ?? '',
    personaType: personaSession?.personaType,
    enabled: checkPermission('PATIENT_LIST_VIEW')
  })

  const patientQuery = usePatientQuery({
    patientId:
      patientsQuery.isLoading ||
      patientsQuery.isError ||
      !patientsQuery.data ||
      !patientsQuery.data.some(({ id }) => id === patientId)
        ? undefined
        : patientId
  })

  const isPatientManager = (patientsQuery.data ?? []).some(
    ({ therapistManagers }) =>
      therapistManagers.some(
        ({ userId: therapistId }) => therapistId === personaSession?.personaId
      )
  )

  return (
    <patientContext.Provider
      value={{
        patient: patientQuery.data,
        patientQuery,
        isPatientManager
      }}
    >
      {children}
    </patientContext.Provider>
  )
}
